// Connectivity.
import { wsAttemptConnect, wsDisconnect } from './actions'
import { WS_CONNECT_FINISHED, WS_DISCONNECT_FINISHED, WS_ERROR } from './actionTypes'
import { getConnectionState } from './selectors'

// Other plugins.
import { getPlatform } from '../../auth/interface/selectors'
import { UNSUBSCRIBE_FINISHED } from '../../subscription/interface/actionTypes'

// Libraries.
import { delay, put, race, select, take } from 'redux-saga/effects'

/**
 * Custom redux-saga effect.
 * Wraps "communication" between the connectivity plugin and another plugin
 *     for connecting to a websocket.
 * @method connectWebsocket
 * @param {Object} websocketInfo Information needed to create the websocket.
 * @param {string} websocketInfo.protocol
 * @param {string} websocketInfo.server
 * @param {string} websocketInfo.port
 * @param {string} websocketInfo.url
 * @param {Object} [websocketInfo.params]
 * @param {string} platform The backend platform being connected to.
 * @return {Object} The response action of type `WS_CONNECT_FINISHED`.
 */
export function * connectWebsocket (websocketInfo, platform) {
  // Dispatch the action that triggers a saga to connect to a websocket.
  yield put(wsAttemptConnect(websocketInfo, platform))

  // Wait for the action that signifies the result of the above action.
  const responseAction = yield take(action => {
    return action.type === WS_CONNECT_FINISHED && action.meta.platform === platform
  })

  // Return the response.
  return responseAction
}

/**
 * Effect for disconnecting a websocket to a platform.
 * @method disconnectWebsocket
 * @param  {Object} payload
 * @param  {string} platform The backend platform being disconnected from.
 * @return {Object} The response action of type `WS_DISCONNECT_FINISHED`.
 */
export function * disconnectWebsocket (payload, platform) {
  // Dispatch the action that triggers a saga to disconnect the websocket.
  yield put(wsDisconnect(payload, platform))

  // Wait for the action that signifies the result of the above action.
  const responseAction = yield take(action => {
    return (action.type === WS_DISCONNECT_FINISHED || action.type === WS_ERROR) && action.meta.platform === platform
  })

  // Return the response.
  return responseAction
}

/**
 * Effect for waiting for the websocket / subscription to reconnect.
 * Assumption is that the websocket is in the middle of reconnect attempts. This
 *    is why the timeout is so long; one of the two scenarios should be guaranteed
 *    to happen before then.
 * @param {number} timeout The time, in milliseconds, to wait before timing out.
 * @return {boolean} Whether the websocket has reconnected or not.
 */
export function * waitForReconnect (timeout = 60000) {
  const platform = yield select(getPlatform)
  const { connected: isConnected } = yield select(getConnectionState, platform)

  if (isConnected) {
    return true
  }

  const { reconnected } = yield race({
    // If websocket reconnects, then we're reconnected (...duh).
    reconnected: take(action => action.type === WS_CONNECT_FINISHED && !action.error),
    // If the user unsubscribes (ie. ws reconnect fails), then we're disconnected.
    disconnected: take(action => action.type === UNSUBSCRIBE_FINISHED && !action.error),
    timeout: delay(timeout)
  })

  return Boolean(reconnected)
}
